import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Dialog, ModalContextExtractor, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { getAllProveedores } from "@/features/auxiliar/proveedores/api/get-all-proveedores";
import { getProveedor } from "@/features/auxiliar/proveedores/api/get-proveedor";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { getAllArticulos } from "@/features/inventario/articulos/api/get-all-articulos";
import { getArticulo } from "@/features/inventario/articulos/api/get-articulo";
import { AddCompraMutation } from "@/features/inventario/compras/api/add-compra";
import { DeleteCompraMutation } from "@/features/inventario/compras/api/delete-compra";
import { getAllCompras, useGetAllComprasQuery } from "@/features/inventario/compras/api/get-all-compras";
import { GetCompraQuery } from "@/features/inventario/compras/api/get-compra";
import CompraForm, { compraFormSchema } from "@/features/inventario/compras/components/compra-form";
import CompraView from "@/features/inventario/compras/components/compra-view";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";

export default function ComprasPage() {
    const openModal = useOpenModal();

    const { monedaBase } = useMonetizacion();

    const hasWritePermiso = useHasPermisos(Permiso.WriteCompras);

    const filterData = useFilters({
        filters: [
            { type: "dateRange", label: "Fecha", fromName: "desde", toName: "hasta" },
            { type: "string", label: "N° de Comprobante", name: "numComprobante" },
            { type: "model", label: "Proveedor", name: "proveedorId", get: getProveedor, getAll: getAllProveedores },
            { type: "model", label: "Artículo", name: "articuloId", get: getArticulo, getAll: getAllArticulos },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllComprasQuery(filterData.filterData);

    const columnData = useColumns({
        columns: [
            { key: "fecha", header: "Fecha", render: row => formatDate(row.fecha), width: "auto" },
            { key: "numComprobante", header: "N° de Comprobante", render: row => formatString(row.numComprobante) },
            { key: "proveedor", header: "Proveedor", render: row => formatString(row.proveedorNombre) },
            { key: "deposito", header: "Depósito", render: row => formatString(row.depositoNombre) },
            { key: "parcialExenta", header: `Parcial Excenta (${monedaBase.simbolo})`, render: row => formatNumber(row.parcialExenta), align: "right", show: false },
            { key: "parcialIvaCinco", header: `Parcial 5% (${monedaBase.simbolo})`, render: row => formatNumber(row.parcialIvaCinco), align: "right", show: false },
            { key: "parcialIvaDiez", header: `Parcial 10% (${monedaBase.simbolo})`, render: row => formatNumber(row.parcialIvaDiez), align: "right", show: false },
            { key: "total", header: `Total (${monedaBase.simbolo})`, render: row => formatNumber(row.total), align: "right" },
            { key: "liquidacionIvaCinco", header: `IVA 5% (${monedaBase.simbolo})`, render: row => formatNumber(row.liquidacionIvaCinco), align: "right", show: false },
            { key: "liquidacionIvaDiez", header: `IVA 10% (${monedaBase.simbolo})`, render: row => formatNumber(row.liquidacionIvaDiez), align: "right", show: false },
            { key: "liquidacionIvaTotal", header: `IVA Total (${monedaBase.simbolo})`, render: row => formatNumber(row.liquidacionIvaTotal), align: "right", show: false },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "creacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "modificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false }
        ],
        key: "compras"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Compras">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.ViewButton onClick={() => openModal("view", row.id)} />
                            {hasWritePermiso && <DataTable.EntryOption.DeleteButton disabled={row.protegido} title="Anular" onClick={() => openModal("delete", row)} />}
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Dialog code="add" size="lg" disableEscapeKeyDown fullHeight>
            <ModalHeader>Agregar Compra</ModalHeader>
            <ModalContextExtractor>
                {modal => <AddCompraMutation>
                    {mutation => <SchemaFormHook
                        schema={compraFormSchema}
                        onSubmit={async ({ value }) => {
                            await mutation.mutateAsync(value);
                            notify("La compra se ha agregado correctamente.");
                        }}
                        submitOnce
                        closeModalOnSubmit
                    >
                        {form => <CompraForm
                            margin={3}
                            form={form}
                            onCancel={modal.handleClose}
                            flex={1}
                        />}
                    </SchemaFormHook>}
                </AddCompraMutation>}
            </ModalContextExtractor>
        </Dialog>

        <Dialog code="view" size="md" fullHeight>
            <ModalHeader>Ver Compra</ModalHeader>
            <ModalValueExtractor>
                {compraId => <GetCompraQuery id={compraId}>
                    {query => <DataFetcher query={query} flex={1}>
                        {compra => <CompraView compra={compra} />}
                    </DataFetcher>}
                </GetCompraQuery>}
            </ModalValueExtractor>
        </Dialog>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {compra => <DeleteCompraMutation id={compra.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Anular Compra "${compra.numComprobante}"`}
                        description={`¿Estás seguro que quieres anular la compra "${compra.numComprobante}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`La compra "${compra.numComprobante}" se ha anulado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteCompraMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCompras}
            defaultFileTitle="Compras"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCompras}
            defaultFileTitle="Compras"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllCompras}
            defaultFileTitle="Compras"
        />
    </ListPage>
}